// src/lib/stem-markup.ts
//
// Parser for the markdown-style markup we allow in question stems.
// Currently supports one construct:
//
//   __underlined text__   →  rendered as an <u> span
//
// The Digital SAT Writing & Language convention is that the portion
// of the sentence the choices replace is underlined in the stem. The
// choices then represent alternatives for that underlined portion;
// Choice A is conventionally "NO CHANGE" (same as the underline).
//
// Why a markdown-ish convention rather than a structured field
// (e.g. `markedRange: [start, end]`):
//   - Stays human-readable in the source data file.
//   - Survives JSON.stringify trivially (it's just a string).
//   - No mutation surface — the stem is still authoritative; we just
//     re-parse it at render time.
//   - Authors writing new questions don't need to count character
//     offsets; they just wrap the underlined portion in `__`.
//
// Returning a structured segment array (not React nodes) keeps the
// parsing logic pure and unit-testable without React Testing Library.
// The caller composes JSX from the segments.

export type StemSegment = {
  type: 'text' | 'underline'
  text: string
}

/**
 * Parse a stem string with `__underlined__` markers into a flat
 * sequence of text + underline segments. Empty segments are dropped.
 *
 * Examples:
 *   parseStem('plain text')
 *     → [{ type: 'text', text: 'plain text' }]
 *
 *   parseStem('before __middle__ after')
 *     → [{ type: 'text', text: 'before ' },
 *        { type: 'underline', text: 'middle' },
 *        { type: 'text', text: ' after' }]
 *
 *   parseStem('two __first__ and __second__ markers')
 *     → [text 'two ', underline 'first', text ' and ',
 *        underline 'second', text ' markers']
 *
 * Edge cases:
 *   - `__` alone (empty underline) is preserved as literal text.
 *     A future stem author who writes `____` gets the literal four
 *     underscores back, not a confusing empty <u> element.
 *   - Single underscores (`x_1`) are preserved as literal text. Only
 *     paired double-underscores are markup.
 *   - Unmatched openers (`__foo` with no closing `__`) are preserved
 *     as literal text. Forgiving over strict — better to render the
 *     raw text than throw at runtime.
 */
export function parseStem(stem: string): StemSegment[] {
  // Non-greedy match between paired `__` delimiters; requires at
  // least one non-underscore char inside so `____` doesn't match.
  const PATTERN = /(__[^_]+?__)/g
  const segments: StemSegment[] = []

  for (const part of stem.split(PATTERN)) {
    if (part.length === 0) continue
    if (part.length > 4 && part.startsWith('__') && part.endsWith('__')) {
      segments.push({ type: 'underline', text: part.slice(2, -2) })
    } else {
      segments.push({ type: 'text', text: part })
    }
  }

  return segments
}

/**
 * Whether a stem contains any markup. Useful for data-validation
 * tests that assert "every W&L question must mark the replaceable
 * portion" — without forcing all questions (e.g. Math) through the
 * same convention.
 */
export function hasUnderlineMarkup(stem: string): boolean {
  return parseStem(stem).some((s) => s.type === 'underline')
}
